<script setup lang="ts">
import { computed } from 'vue';

import { AppCircleCheckbox } from '@libs/ui';

import type { TicketOption } from '../models/changeTicket';
import {
  BracketType,
  RelativeMoneyFormatter,
} from '../../../core/components/formatters';

interface TicketOptionCardProps {
  ticketOption: TicketOption;
  selectedTicketType: string;
  basePrice: number;
}

const props = defineProps<TicketOptionCardProps>();

interface TicketOptionCardEmits {
  (eventName: 'selectTicketType', type: string): void;
}

const emit = defineEmits<TicketOptionCardEmits>();

const isSelected = computed(
  () => props.ticketOption.type === props.selectedTicketType
);
</script>

<template>
  <div
    class="ticket-option"
    :class="{ selected: isSelected }"
    @click="emit('selectTicketType', props.ticketOption.type)"
  >
    <div class="ticket-option-header">
      <div class="header-text">{{ props.ticketOption.type }}</div>
      <AppCircleCheckbox :checked="isSelected" theme="dark" />
    </div>
    <div class="description">{{ props.ticketOption.description }}</div>
    <div v-if="!isSelected" class="price">
      <RelativeMoneyFormatter
        :data="{
          price: ticketOption.price,
          basePrice: props.basePrice,
          color: 'color-secondary-40',
          font: 'font-label-xxl-bold',
          bracketType: BracketType.None,
        }"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.ticket-option {
  background: var(--color-secondary-95);
  color: var(--color-secondary-40);
  border-radius: var(--border-radius-12);
  padding: var(--spacing-16);
  cursor: pointer;
  transition: background var(--transition-medium);

  &.selected {
    background: var(--color-primary-30);
    color: var(--color-white);
    @include on-hover {
      background: var(--color-primary-20);
    }
  }
  @include on-hover {
    background: var(--color-secondary-93);
  }

  .ticket-option-header {
    display: flex;
    justify-content: space-between;

    .header-text {
      @include font-label-xxl-bold;
      margin-bottom: var(--spacing-16);
    }
  }

  .description {
    @include font-body-s-light;
  }

  .price {
    margin-top: var(--spacing-16);
  }
}
</style>
